import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { OrderedSet, Record, Map } from 'immutable';

import { ConsumerMetadataItemEntity } from '../components/exposition-consumers-mapping/consumer-mapping-popup/consumer-metadata-item.entity';
import { ExpositionHistoryListEntity } from '../components/exposition-history-list/exposition-history-list.entity';
import { PublicationAction } from '../components/exposition-list/exposition-item.entity';
import { ExpositionListEntity } from '../components/exposition-list/exposition-list.entity';
import { MigrationReadyExpositionList } from '../components/exposition-migrations-list/exposition-migration-stepper/migration-ready-exposition-list.entity';
import { MigratedExpositionListEntity } from '../components/exposition-migrations-list/migrated-exposition-list.entity';
import { ExpositionDatablockColumnEntity } from '../components/exposition-stepper/exposition-datablock-list/exposition-datablock-column.entity';
import { ExpositionDatablockListEntity } from '../components/exposition-stepper/exposition-datablock-list/exposition-datablock-list.entity';
import { ExpositionEntity } from '../components/exposition-view/exposition.entity';
import { ExpositionColumnConfigListEntity } from '../domain/exposition-column-config-list.entity';
import { ExpositionConsumerMappingEntity } from '../domain/exposition-consumer-mapping.entity';
import { ExpositionConsumerEntity } from '../domain/exposition-consumer.entity';
import { ExpositionPermissionsEntity } from '../domain/exposition-permissions.entity';
export const ExpositionSliceKey = 'all';

interface IExpositionOriginalSliceState {
	loading: boolean;
	isLoadingDatablockColumns: boolean;
	isSavingExposition: boolean;
	mappingRequestId: string;
	isLoadingJobErrorLogs: boolean;
	isLoadingMetadataInfo: boolean;
	actionInProgress: PublicationAction;
	permissions: ExpositionPermissionsEntity;
	availableTags: OrderedSet<AppTagItemEntity>; // FIXME: move to core state
	availableConsumers: Array<ExpositionConsumerEntity>; // FIXME: move to core state
	eligibleConsumers: {
		consumers: Map<string, ConsumerMetadataItemEntity>;
		total: number;
	};
	expositions: ExpositionListEntity;
	v1Expositions: MigrationReadyExpositionList;
	v1Consumers: Array<ExpositionConsumerMappingEntity>;
	migrated: MigratedExpositionListEntity;
	inViewExposition: ExpositionEntity;
	history: ExpositionHistoryListEntity;
	stepper: {
		columns: ExpositionColumnConfigListEntity;
		datablocks: ExpositionDatablockListEntity;
	};
}

const stateDefaults: IExpositionOriginalSliceState = {
	loading: false,
	isLoadingDatablockColumns: false,
	isSavingExposition: false,
	isLoadingJobErrorLogs: false,
	isLoadingMetadataInfo: false,
	actionInProgress: 'none',
	v1Consumers: [],
	mappingRequestId: '',
	permissions: ExpositionPermissionsEntity.build({}),
	availableConsumers: [],
	eligibleConsumers: {
		consumers: Map(),
		total: 0,
	},
	availableTags: OrderedSet(),
	expositions: ExpositionListEntity.build([]),
	v1Expositions: MigrationReadyExpositionList.build([]),
	migrated: MigratedExpositionListEntity.build([]),
	inViewExposition: ExpositionEntity.build(),
	history: ExpositionHistoryListEntity.build([]),
	stepper: {
		columns: ExpositionColumnConfigListEntity.build([]),
		datablocks: ExpositionDatablockListEntity.build([]),
	},
};

export class ExpositionOriginalSliceState extends Record<IExpositionOriginalSliceState>(
	stateDefaults
) {
	public static readonly key = ExpositionSliceKey;

	public toggleLoading(isLoading: boolean): ExpositionOriginalSliceState {
		return this.set('loading', isLoading);
	}

	// FIXME: remove
	public setActionInProgress(
		action: PublicationAction
	): ExpositionOriginalSliceState {
		return this.set('actionInProgress', action);
	}

	public toggleIsSavingExposition(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isSavingExposition', isLoading);
	}

	public toggleLoadingDatablockColumns(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isLoadingDatablockColumns', isLoading);
	}

	public updateDatablockListWithColumns(
		datablockId: number,
		columns: Array<ExpositionDatablockColumnEntity>
	): ExpositionOriginalSliceState {
		const datablocks = this.stepper.datablocks.setDatablockColumns(
			datablockId,
			columns
		);
		const stepper = {
			...this.stepper,
			datablocks,
		};
		return this.set('stepper', stepper);
	}

	public setColumnConfigList(
		columns: ExpositionColumnConfigListEntity
	): ExpositionOriginalSliceState {
		return this.set('stepper', {
			...this.stepper,
			columns,
		});
	}

	public setDatablockList(
		datablocks: ExpositionDatablockListEntity
	): ExpositionOriginalSliceState {
		return this.set('stepper', {
			...this.stepper,
			datablocks,
		});
	}

	public setAvailableConsumers(
		availableConsumers: Array<ExpositionConsumerEntity>
	): ExpositionOriginalSliceState {
		return this.set('availableConsumers', availableConsumers);
	}

	public setEligibleConsumers(
		consumers: Map<string, ConsumerMetadataItemEntity>,
		total: number
	): ExpositionOriginalSliceState {
		return this.set('eligibleConsumers', {
			consumers,
			total,
		});
	}

	public setTags(
		tags: OrderedSet<AppTagItemEntity>
	): ExpositionOriginalSliceState {
		return this.set('availableTags', tags);
	}

	public resetStepper(): ExpositionOriginalSliceState {
		return this.merge(stateDefaults);
	}

	public setExpositionList(
		list: ExpositionListEntity
	): ExpositionOriginalSliceState {
		return this.set('expositions', list);
	}

	public setInViewExposition(
		expo: ExpositionEntity
	): ExpositionOriginalSliceState {
		return this.set('inViewExposition', expo);
	}

	public setCanCreateExposition(
		hasPermission: boolean
	): ExpositionOriginalSliceState {
		return this.set(
			'permissions',
			this.get('permissions').setHasCreateGlobalPermission(hasPermission)
		);
	}

	public setExpositionViewPermissions(permissions: {
		delete: boolean;
		view: boolean;
		edit: boolean;
	}): ExpositionOriginalSliceState {
		return this.set(
			'permissions',
			this.get('permissions').setExpositionViewPermissions(permissions)
		);
	}

	public setV1Expositions(
		list: MigrationReadyExpositionList
	): ExpositionOriginalSliceState {
		return this.set('v1Expositions', list);
	}

	public setUnmappedConsumers(
		consumers: Array<ExpositionConsumerMappingEntity>
	): ExpositionOriginalSliceState {
		return this.set('v1Consumers', consumers);
	}

	public setRequestId(mappingRequestId: string): ExpositionOriginalSliceState {
		return this.set('mappingRequestId', mappingRequestId);
	}

	public setMigrated(
		list: MigratedExpositionListEntity
	): ExpositionOriginalSliceState {
		return this.set('migrated', list);
	}

	public setHistory(
		list: ExpositionHistoryListEntity
	): ExpositionOriginalSliceState {
		return this.set('history', list);
	}
	public setIsLoadingErrorLogs(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isLoadingJobErrorLogs', isLoading);
	}

	public toggleLoadingMetadataInfo(
		isLoading: boolean
	): ExpositionOriginalSliceState {
		return this.set('isLoadingMetadataInfo', isLoading);
	}

	public resetEligibleConsumers(): ExpositionOriginalSliceState {
		return this.set('eligibleConsumers', stateDefaults.eligibleConsumers);
	}

	public resetConsumersToMap(): ExpositionOriginalSliceState {
		return this.set('v1Consumers', stateDefaults.v1Consumers);
	}
}
